/**
 * Global Leaderboard Utilities
 * Calculates lifetime winnings across all events
 */

import { getAllEventIds, loadEvent } from './persistence';

export interface GlobalLeaderboardEntry {
  userId: string;
  username: string;
  totalWinnings: number;
  eventsParticipated: number;
  eventsWon: number; // Events where user finished #1
}

export interface UserEventResult {
  eventId: string;
  eventName: string;
  participantId: string;
  participantName: string;
  winningsBalance: number;
  rank: number;
  totalBets: number;
  wonBets: number;
  finishedAt?: number;
}

/**
 * Calculate global leaderboard with lifetime winnings
 */
export async function calculateGlobalLeaderboard(): Promise<GlobalLeaderboardEntry[]> {
  const eventIds = await getAllEventIds();
  const userWinnings: Map<string, {
    username: string;
    totalWinnings: number;
    events: Set<string>;
    eventsWon: Set<string>;
  }> = new Map();

  // Process all events
  for (const eventId of eventIds) {
    try {
      const event = await loadEvent(eventId);
      if (!event) continue;

      // Calculate ranks for this event
      const sortedParticipants = [...event.participants].sort(
        (a, b) => (b.winningsBalance || 0) - (a.winningsBalance || 0)
      );

      // Process each participant
      // Users are identified by participant name (Venus handles authentication)
      event.participants.forEach((participant) => {
        // Use participant name as key (Venus users are identified by their profile)
        const key = participant.name.toLowerCase();
        
        if (!userWinnings.has(key)) {
          userWinnings.set(key, {
            username: participant.name,
            totalWinnings: 0,
            events: new Set(),
            eventsWon: new Set(),
          });
        }

        const userData = userWinnings.get(key)!;
        userData.totalWinnings += participant.winningsBalance || 0;
        userData.events.add(eventId);

        // Check if user won this event (rank #1)
        const rank = sortedParticipants.findIndex(p => p.id === participant.id) + 1;
        if (rank === 1) {
          userData.eventsWon.add(eventId);
        }
      });
    } catch (err) {
      console.error(`Error processing event ${eventId}:`, err);
    }
  }

  // Convert to array and sort
  const entries: GlobalLeaderboardEntry[] = Array.from(userWinnings.entries())
    .filter(([_, data]) => data.totalWinnings > 0 || data.events.size > 0) // Only include users with activity
    .map(([userId, data]) => ({
      userId,
      username: data.username,
      totalWinnings: data.totalWinnings,
      eventsParticipated: data.events.size,
      eventsWon: data.eventsWon.size,
    }));

  // Sort by total winnings (descending)
  return entries.sort((a, b) => b.totalWinnings - a.totalWinnings);
}

/**
 * Get user's event results
 */
export async function getUserEventResults(userId: string): Promise<UserEventResult[]> {
  const eventIds = await getAllEventIds();
  const results: UserEventResult[] = [];

  // userId can be either a Venus user ID or a participant name
  // Find events where user participated (by participant ID or name match)
  for (const eventId of eventIds) {
    try {
      const event = await loadEvent(eventId);
      if (!event) continue;

      // Try to find participant by ID first, then by name
      let participant = event.participants.find(p => p.id === userId);
      if (!participant) {
        // If not found by ID, try to find by name (userId might be username)
        participant = event.participants.find(
          p => p.name.toLowerCase() === userId.toLowerCase()
        );
      }
      if (!participant) continue;

      // Calculate rank
      const sortedParticipants = [...event.participants].sort(
        (a, b) => (b.winningsBalance || 0) - (a.winningsBalance || 0)
      );
      const rank = sortedParticipants.findIndex(p => p.id === participant.id) + 1;

      // Count bets
      const totalBets = participant.bets?.length || 0;
      const wonBets = participant.bets?.filter(b => b.status === 'won').length || 0;

      results.push({
        eventId: event.id,
        eventName: event.name,
        participantId: participant.id,
        participantName: participant.name,
        winningsBalance: participant.winningsBalance || 0,
        rank,
        totalBets,
        wonBets,
        finishedAt: event.finishedAt,
      });
    } catch (err) {
      console.error(`Error processing event ${eventId}:`, err);
    }
  }

  // Sort by finished date (most recent first)
  return results.sort((a, b) => {
    const aTime = a.finishedAt || 0;
    const bTime = b.finishedAt || 0;
    return bTime - aTime;
  });
}

